import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import styles from './BriefGuide.module.css'

/*
 * A short, optional walkthrough of the Health check brief. Each step spotlights the part of the page it
 * talks about (elements marked with data-guide="…"): everything else is dimmed and blurred, the
 * highlighted element itself is never restyled or moved, and the page holds still until the guide closes.
 */

export interface Step {
  target: string
  title: string
  body: string
  /** one line, for the diagram tour */
  short: string
}

export const STEPS: Step[] = [
  {
    target: 'metrics',
    title: 'Start with the headline numbers',
    short: 'Fleet value and risk at a glance',
    body:
      'Estimated fleet value and risk sum up the account. Select the info icon on either to see how it was worked out.',
  },
  {
    target: 'notes',
    title: 'Read the AI notes',
    short: 'What the data means, beside each section',
    body:
      'Notes sit beside each section and explain what the data means for this customer. Use Show more for the full reasoning.',
  },
  {
    target: 'mission',
    title: 'Ask Mission Control',
    short: 'Ask follow-ups or draft outreach',
    body:
      'Ask follow-up questions about this customer, or start from a suggested prompt. It already has the brief and print data.',
  },
  {
    target: 'view',
    title: 'Make it yours',
    short: 'Switch layout, hide notes or print',
    body:
      'Switch between side-by-side and stacked, hide the notes, or print the brief to take into a customer conversation.',
  },
]

/* the Mission Control step takes the assistant's chosen name (a demo option) */
export const stepTitle = (step: Step, missionTitle?: string): string =>
  step.target === 'mission' && missionTitle ? missionTitle : step.title

export const GUIDE_SEEN_KEY = 'health_check_guide_seen_v2'

export const hasSeenGuide = (): boolean => {
  try {
    return localStorage.getItem(GUIDE_SEEN_KEY) === 'true'
  } catch {
    return true
  }
}

interface Hole {
  l: number
  t: number
  w: number
  h: number
  k: number
}

interface Props {
  onClose: () => void
  /** the tour step for the AI assistant, under the name it's been given */
  missionTitle?: string
}

export const BriefGuide: FunctionComponent<Props> = ({ onClose, missionTitle }) => {
  const [step, setStep] = useState(0)
  const current = STEPS[step]
  const last = step === STEPS.length - 1

  // one blurred pane over the page, with a rounded hole cut out where the step's element is
  const paneRef = useRef<HTMLDivElement>(null)
  const guideRef = useRef<HTMLDivElement>(null)

  // bring the step's element into view once, when the step changes
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-guide="${current.target}"]`)
    if (!el || typeof el.scrollIntoView !== 'function') return
    const r = el.getBoundingClientRect()
    if (r.top < 80 || r.bottom > window.innerHeight - 80) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [current.target])

  // the page doesn't scroll while the guide is open (the guide moves it to each step itself)
  useEffect(() => {
    const SCROLL_KEYS = [' ', 'PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown']
    const inGuide = (t: EventTarget | null) => t instanceof Node && !!guideRef.current?.contains(t)
    const block = (e: Event) => {
      if (!inGuide(e.target)) e.preventDefault()
    }
    const blockKeys = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const typing = !!t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(t.tagName))
      if (!typing && SCROLL_KEYS.includes(e.key)) e.preventDefault()
    }
    window.addEventListener('wheel', block, { passive: false })
    window.addEventListener('touchmove', block, { passive: false })
    window.addEventListener('keydown', blockKeys)
    return () => {
      window.removeEventListener('wheel', block)
      window.removeEventListener('touchmove', block)
      window.removeEventListener('keydown', blockKeys)
    }
  }, [])

  // keep the hole on the element as the page resizes or animates. When the step changes, the hole glides
  // from where it was to the new element over a short eased move; after that it tracks the element exactly.
  const holeRef = useRef<Hole | null>(null)
  useEffect(() => {
    if (typeof window.requestAnimationFrame !== 'function') return undefined
    const PAD = 10
    const DURATION = 380
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const from = holeRef.current
    const start = performance.now()
    let raf = 0
    let prevKey = ''
    const place = (now: number) => {
      raf = window.requestAnimationFrame(place)
      const pane = paneRef.current
      if (!pane) return
      let el = document.querySelector<HTMLElement>(`[data-guide="${current.target}"]`)
      // a wrapper around a single component (e.g. Mission Control's column) is measured by that component
      if (el && el.children.length === 1) el = el.children[0] as HTMLElement
      const vw = window.innerWidth
      const vh = window.innerHeight
      const r = el ? el.getBoundingClientRect() : null
      // nothing to show: the hole shrinks to a point in the middle, so the whole page is dimmed
      const target: Hole =
        el && r && r.width > 0 && r.height > 0
          ? {
              l: r.left - PAD,
              t: r.top - PAD,
              w: r.width + PAD * 2,
              h: r.height + PAD * 2,
              k: 4,
            }
          : { l: vw / 2, t: vh / 2, w: 0, h: 0, k: 0 }
      const p = !from || reduced ? 1 : Math.min(1, (now - start) / DURATION)
      const e = 1 - (1 - p) ** 3 // ease out
      const mix = (a: number, b: number) => (from ? a + (b - a) * e : b)
      const hole: Hole = from
        ? {
            l: mix(from.l, target.l),
            t: mix(from.t, target.t),
            w: mix(from.w, target.w),
            h: mix(from.h, target.h),
            k: mix(from.k, target.k),
          }
        : target
      holeRef.current = hole
      const { l, t, w, h } = hole
      const k = Math.max(0, Math.min(hole.k, w / 2, h / 2))
      const f = (n: number) => n.toFixed(1)
      const cut =
        w > 0 && h > 0
          ? ` M${f(l + k)} ${f(t)} H${f(l + w - k)} A${f(k)} ${f(k)} 0 0 1 ${f(l + w)} ${f(t + k)}` +
            ` V${f(t + h - k)} A${f(k)} ${f(k)} 0 0 1 ${f(l + w - k)} ${f(t + h)} H${f(l + k)}` +
            ` A${f(k)} ${f(k)} 0 0 1 ${f(l)} ${f(t + h - k)} V${f(t + k)} A${f(k)} ${f(k)} 0 0 1 ${f(l + k)} ${f(t)} Z`
          : ''
      // the outer edge runs well past the viewport, so the blur never shows a soft edge at the corners
      const key = `M-100 -100 H${vw + 100} V${vh + 100} H-100 Z${cut}`
      if (key === prevKey) return
      prevKey = key
      pane.style.clipPath = `path(evenodd, "${key}")`
    }
    place(start)
    return () => window.cancelAnimationFrame(raf)
  }, [current.target])

  const close = () => {
    try {
      localStorage.setItem(GUIDE_SEEN_KEY, 'true')
    } catch {
      // storage unavailable; the guide may show again next time
    }
    onClose()
  }

  // cards are stacked: the current step in front, the next two peeking out behind it, finished ones slid away
  const position = (i: number) => (i < step ? 'past' : i - step > 2 ? 'hidden' : String(i - step))

  return (
    <>
      <div ref={paneRef} className={styles.spotlight} aria-hidden='true' />
      <div ref={guideRef} className={styles.guide} role='region' aria-label='Tour' aria-live='polite'>
        {STEPS.map((s, i) => {
          const front = i === step
          return (
            <section key={s.target} className={styles.card} data-pos={position(i)} aria-hidden={!front}>
              <h4 className={styles.title}>{stepTitle(s, missionTitle)}</h4>
              <p className={styles.body}>{s.body}</p>
              {front && (
                <footer className={styles.footer}>
                  {/* leave at any point; on the last step Done does the same */}
                  {!last && (
                    <button type='button' className={styles.skip} onClick={close}>
                      Skip tour
                    </button>
                  )}
                  <span className={styles.footerSpacer} />
                  {step > 0 && (
                    <button type='button' className='btn secondary sml' onClick={() => setStep(step - 1)}>
                      Back
                    </button>
                  )}
                  <button type='button' className='btn sml' onClick={last ? close : () => setStep(step + 1)}>
                    {last ? 'Done' : 'Next'}
                  </button>
                </footer>
              )}
            </section>
          )
        })}
      </div>
    </>
  )
}
